import { func, object, arrayOf } from 'prop-types';
import { path, pick } from 'ramda';
import React from 'react';

import { colors } from 'styles/provider';

import ContentColumns from '../../components/ContentColumns';
import TextInputWithSuggestions from '../../components/TextInputWithSuggestions';
import SummaryTable from '../../components/SummaryTable';
import { DiaryWeeklyRecapChart, weekSummaryTableContent } from '../Charts';
import { subtitle, descriptionText } from './View.less';
import { getLatestFinishedCommitData } from 'lib/documentHandling';
import { formatPartialDate, isPartialDate } from 'neuro-utils';

// Placeholders, change these names to something more descriptive when they are defined somewhere
const suggestionsStrong = [
  'suggestionStrong1',
  'suggestionStrong2',
  'suggestionStrong3',
  'suggestionStrong4',
  'suggestionStrong5',
  'suggestionStrong6',
];
const suggestionsMild = [
  'suggestionMild1',
  'suggestionMild2',
  'suggestionMild3',
  'suggestionMild4',
  'suggestionMild5',
  'suggestionMild6',
];
const suggestionsLocale = 'onlineRehabilitation.opts.';

const View1 = ({ documents }, { fmString, formValues, onChange }) => {
  const getValue = (name, index) => path(['rehab3', name, index], formValues) || '';

  const onChangeFactors = (name, index) => event => {
    const allValues = path(['rehab3', name], formValues) || ['', '', ''];
    const eventValue = path(['currentTarget', 'value'], event);
    // Copy array so that changes dont roll backwards
    const allValuesMapped = allValues.map(a => a);
    allValuesMapped[index] = eventValue;
    onChange({ [name]: allValuesMapped });
  };

  const fatigueDiaryDocument = documents
    .filter(d => d.documentType === 'fatigueTrackingDiary')
    .sort((a, b) => b.createDate - a.createDate)[0];
  const fatigueData = fatigueDiaryDocument && getLatestFinishedCommitData(fatigueDiaryDocument);

  const includedDayKeys = ['0', '1', '2', '3', '4', '5', '6'];
  const diaryDays = pick(includedDayKeys, fatigueData);
  const days = Object.values(diaryDays);

  const weekSummaryTableContentArray = weekSummaryTableContent(days, fmString);

  return (
    <React.Fragment>
      <ContentColumns
        firstColumn={
          <div>
            <h2>{fmString('onlineRehabilitation.rehab3.1.welcome')}</h2>
            <br />
            <div>{fmString('onlineRehabilitation.rehab3.1.text')}</div>
            <br />
            <h3 style={{ textTransform: 'uppercase' }}>
              {fmString('onlineRehabilitation.rehab3.1.recapHeader', {
                date1: isPartialDate(path([0, 'date'], fatigueData))
                  ? formatPartialDate(path([0, 'date'], fatigueData))
                  : '',
                date2: isPartialDate(path([6, 'date'], fatigueData))
                  ? formatPartialDate(path([6, 'date'], fatigueData))
                  : '',
              })}
            </h3>
            <br />
          </div>
        }
      />
      <ContentColumns
        firstColumn={<div>{path(['startDate'], fatigueData) && <DiaryWeeklyRecapChart diary={fatigueData} />}</div>}
        secondColumn={
          <div style={{ paddingBottom: 20 }}>
            <SummaryTable content={weekSummaryTableContentArray} />
          </div>
        }
        alignEnd
      />
      <ContentColumns
        firstColumn={
          <div>
            <div style={{ margin: '0.5rem 0 0 0', color: colors.clrDarkGray }}>
              {fmString('onlineRehabilitation.rehab3.1.recapDescription')}
            </div>
            <div>{fmString('onlineRehabilitation.rehab3.1.recapText')}</div>
            <br />
            <br />
            <h3>{fmString('onlineRehabilitation.rehab3.1.headerStrongFatigue')}</h3>
            <div className={descriptionText}>{fmString('onlineRehabilitation.suggestionTextBoxDescription')}</div>
            <TextInputWithSuggestions
              onChange={onChangeFactors('strongFatiqueFactors', 0)}
              value={getValue('strongFatiqueFactors', 0)}
              name={'strongFatiqueFactors'}
              textPlaceholder={fmString('onlineRehabilitation.rehab3.1.strongFatigueTextPlaceholder', { n: 1 })}
              suggestions={suggestionsStrong}
              suggestionsLocale={suggestionsLocale}
            />
            <TextInputWithSuggestions
              onChange={onChangeFactors('strongFatiqueFactors', 1)}
              value={getValue('strongFatiqueFactors', 1)}
              name={'strongFatiqueFactors1'}
              textPlaceholder={fmString('onlineRehabilitation.rehab3.1.strongFatigueTextPlaceholder', { n: 2 })}
              suggestions={suggestionsStrong}
              suggestionsLocale={suggestionsLocale}
            />
            <TextInputWithSuggestions
              onChange={onChangeFactors('strongFatiqueFactors', 2)}
              value={getValue('strongFatiqueFactors', 2)}
              name={'strongFatiqueFactors2'}
              textPlaceholder={fmString('onlineRehabilitation.rehab3.1.strongFatigueTextPlaceholder', { n: 3 })}
              suggestions={suggestionsStrong}
              suggestionsLocale={suggestionsLocale}
            />
            <br />
            <div className={subtitle}>{fmString('onlineRehabilitation.rehab3.1.headerMildFatigue')}</div>
            <div className={descriptionText}>{fmString('onlineRehabilitation.suggestionTextBoxDescription')}</div>
            <TextInputWithSuggestions
              color="green"
              onChange={onChangeFactors('mildFatiqueFactors', 0)}
              value={getValue('mildFatiqueFactors', 0)}
              name={'mildFatiqueFactors'}
              textPlaceholder={fmString('onlineRehabilitation.rehab3.1.mildFatigueTextPlaceholder', { n: 1 })}
              suggestions={suggestionsMild}
              suggestionsLocale={suggestionsLocale}
            />
            <TextInputWithSuggestions
              color="green"
              onChange={onChangeFactors('mildFatiqueFactors', 1)}
              value={getValue('mildFatiqueFactors', 1)}
              name={'mildFatiqueFactors'}
              textPlaceholder={fmString('onlineRehabilitation.rehab3.1.mildFatigueTextPlaceholder', { n: 2 })}
              suggestions={suggestionsMild}
              suggestionsLocale={suggestionsLocale}
            />
            <TextInputWithSuggestions
              color="green"
              onChange={onChangeFactors('mildFatiqueFactors', 2)}
              value={getValue('mildFatiqueFactors', 2)}
              name={'mildFatiqueFactors'}
              textPlaceholder={fmString('onlineRehabilitation.rehab3.1.mildFatigueTextPlaceholder', { n: 3 })}
              suggestions={suggestionsMild}
              suggestionsLocale={suggestionsLocale}
            />
          </div>
        }
      />
    </React.Fragment>
  );
};

View1.contextTypes = {
  fmString: func,
  formValues: object,
  onChange: func,
};
View1.propTypes = {
  documents: arrayOf(object),
};

export default View1;
